<template>
  <v-dialog
    :model-value="modelValue"
    max-width="440"
    :persistent="busy"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <v-card class="doff">
      <!-- Header -->
      <div class="doff-head">
        <div class="ic"><v-icon>mdi-beach</v-icon></div>
        <div class="t">{{ t('dayOff.title') }}</div>
        <v-btn icon="mdi-close" variant="text" size="small" :disabled="busy" @click="close" />
      </div>

      <div class="doff-body">
        <!-- Instructor -->
        <div class="label">{{ t('dayOff.instructor') }}</div>
        <v-select
          v-model="form.instructorId"
          :items="instructors"
          item-title="fio"
          item-value="id"
          density="comfortable"
          variant="outlined"
          hide-details
          class="mb-4"
          prepend-inner-icon="mdi-account-tie"
        />

        <!-- Mode toggle: single day vs range -->
        <v-btn-toggle
          v-model="mode"
          mandatory
          density="comfortable"
          variant="outlined"
          divided
          class="mb-4 mode-toggle"
        >
          <v-btn value="single" size="small">{{ t('dayOff.singleDay') }}</v-btn>
          <v-btn value="range" size="small">{{ t('dayOff.range') }}</v-btn>
        </v-btn-toggle>

        <!-- Single date -->
        <template v-if="mode === 'single'">
          <div class="label">{{ t('dayOff.date') }}</div>
          <input v-model="form.startDate" type="date" class="date-input mb-4" />

          <!-- Whole day vs specific hours -->
          <div class="d-flex align-center justify-space-between mb-2">
            <span class="label mb-0">{{ t('dayOff.wholeDay') }}</span>
            <v-switch
              v-model="form.wholeDay"
              color="#b08d57"
              density="compact"
              hide-details
              inset
            />
          </div>
          <div v-if="!form.wholeDay" class="d-flex ga-3 mb-4">
            <div style="flex:1">
              <div class="label">{{ t('dayOff.timeFrom') }}</div>
              <input v-model="form.startTime" type="time" step="900" class="date-input" />
            </div>
            <div style="flex:1">
              <div class="label">{{ t('dayOff.timeTo') }}</div>
              <input v-model="form.endTime" type="time" step="900" :min="form.startTime" class="date-input" />
            </div>
          </div>
          <div v-else class="mb-4"></div>
        </template>

        <!-- Range -->
        <template v-else>
          <div class="d-flex ga-3 mb-4">
            <div style="flex:1">
              <div class="label">{{ t('dayOff.from') }}</div>
              <input v-model="form.startDate" type="date" class="date-input" />
            </div>
            <div style="flex:1">
              <div class="label">{{ t('dayOff.to') }}</div>
              <input v-model="form.endDate" type="date" :min="form.startDate" class="date-input" />
            </div>
          </div>
        </template>

        <!-- Reason -->
        <div class="label">{{ t('dayOff.reason') }}</div>
        <v-text-field
          v-model="form.reason"
          :placeholder="t('dayOff.reasonPlaceholder')"
          density="comfortable"
          variant="outlined"
          hide-details
          prepend-inner-icon="mdi-comment-text-outline"
        />
      </div>

      <div class="doff-foot">
        <v-btn variant="text" :disabled="busy" @click="close">{{ t('common.cancel') }}</v-btn>
        <v-btn color="#b08d57" :loading="busy" :disabled="!canSave" @click="submit">
          {{ t('dayOff.save') }}
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, reactive, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  modelValue:          { type: Boolean, default: false },
  instructors:         { type: Array,   default: () => [] },
  defaultInstructorId: { type: [Number, String], default: null },
  defaultDate:         { type: String,  default: '' },
  busy:                { type: Boolean, default: false }
})
const emit = defineEmits(['update:modelValue', 'submit'])

const { t } = useI18n()

const mode = ref('single')
const form = reactive({ instructorId: null, startDate: '', endDate: '', reason: '', wholeDay: true, startTime: '09:00', endTime: '18:00' })

const reset = () => {
  form.instructorId = props.defaultInstructorId ?? props.instructors[0]?.id ?? null
  form.startDate = props.defaultDate || ''
  form.endDate = props.defaultDate || ''
  form.reason = ''
  form.wholeDay = true
  form.startTime = '09:00'
  form.endTime = '18:00'
  mode.value = 'single'
}

watch(() => props.modelValue, (open) => { if (open) reset() })
// Keep endDate >= startDate in range mode
watch(() => form.startDate, (v) => { if (mode.value === 'range' && form.endDate && form.endDate < v) form.endDate = v })
// Keep endTime > startTime
watch(() => form.startTime, (v) => { if (!form.wholeDay && form.endTime && form.endTime <= v) form.endTime = v })

const canSave = computed(() => {
  if (!form.instructorId || !form.startDate) return false
  if (mode.value === 'range' && !form.endDate) return false
  if (mode.value === 'single' && !form.wholeDay) {
    if (!form.startTime || !form.endTime || form.endTime <= form.startTime) return false
  }
  return true
})

const close = () => emit('update:modelValue', false)

const submit = () => {
  if (!canSave.value) return
  const partial = mode.value === 'single' && !form.wholeDay
  emit('submit', {
    instructorId: form.instructorId,
    startDate: form.startDate,
    endDate: mode.value === 'single' ? form.startDate : form.endDate,
    startTime: partial ? form.startTime : '',
    endTime: partial ? form.endTime : '',
    reason: form.reason.trim()
  })
}
</script>

<style scoped>
.doff-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 16px 8px;
}
.doff-head .ic {
  width: 40px; height: 40px;
  border-radius: 10px;
  display: grid; place-items: center;
  background: rgba(176, 141, 87, 0.15);
  color: #b08d57;
}
.doff-head .t { font-size: 1.1rem; font-weight: 600; flex: 1; }
.doff-body { padding: 8px 16px 4px; }
.doff-foot {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px 16px;
}
.label {
  font-size: .78rem;
  font-weight: 600;
  color: rgba(var(--v-theme-on-surface), 0.6);
  margin-bottom: 6px;
}
.mode-toggle { width: 100%; }
.mode-toggle .v-btn { flex: 1; }
.date-input {
  width: 100%;
  padding: 11px 14px;
  font-size: 1rem;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.38);
  border-radius: 8px;
  background: transparent;
  color: rgb(var(--v-theme-on-surface));
  color-scheme: dark;
}
.date-input:focus { outline: none; border-color: #b08d57; }
</style>
